import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { isEmpty, pick, pipe, reject, isNil } from 'ramda'
import { FormikContextType, useFormikContext } from 'formik'
import { IconSparkle, TextLink, TextareaAction } from '@machobear/design-system'

import { TextArea } from '@/components/ds-form'
import { toast } from '@/components/toast-notifications'
import { showUpdateGenerateTextModal } from '@/features/SmartDescription/generate-text'
import { showChangeToneModal } from '@/features/SmartDescription'

import { FormSection } from '../../components'
import InfoBox from '../../components/info-box'
import { DetailsFormValues } from '../../types'

const GENERATE_KEYS = [
  'building_id',
  'count_bedrooms',
  'count_bathrooms',
  'count_dens',
  'size',
  'max_occupancy',
  'furnished',
  'allow_cats',
  'allow_dogs',
  'allow_pets',
  'allow_smoking',
  'availability_date'
]

const DescriptionField = () => {
  const { t } = useTranslation()
  const { listingId } = useParams<{ listingId: string }>()
  const { values, setFieldValue }: FormikContextType<DetailsFormValues> =
    useFormikContext<DetailsFormValues>()

  // @ts-ignore
  const generateValues = pipe(pick(GENERATE_KEYS), reject(isEmpty), reject(isNil))(values)

  const setDescriptionFormValue = (value: string) => {
    setFieldValue('description', value)
  }

  const handleEmptyDescription = () =>
    toast(
      t(
        'l.smart_description.change_tone.empty_description.error',
        'Please enter a description to continue and try again.'
      ),
      {
        type: 'warning',
        persistent: true
      }
    )

  const handleClickGenerateText = () => {
    showChangeToneModal({
      listingId,
      formValues: generateValues,
      initAction: 'create',
      heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
      setDescriptionFormValue
    })
  }

  const handleClickChangeTone = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      showChangeToneModal({
        description: values?.description,
        listingId,
        heading: t('l.smart_description.change_tone.change_tone.title', 'Change tone'),
        setDescriptionFormValue
      })
    }
  }

  const handleClickCleanUp = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      showUpdateGenerateTextModal({
        description: values?.description,
        listingId,
        tone: 'cleanup',
        heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
        setDescriptionFormValue,
        eventId: 'application-description-ai-cleanup-click'
      })
    }
  }

  return (
    <FormSection label={t('Description')}>
      <TextArea
        name="description"
        label={t(
          'r.rental_dashboard.edit.description.placeholder',
          'Include additional information about your listing'
        )}
        lines={5}
        lineLimit={14}
        characterLimit={2000}
        onChange={e => {
          setDescriptionFormValue(e.target.value)
        }}
        actions={[
          <TextareaAction.Button
            id="application-description-ai-gentext"
            label={t('l.smart_description.generate_text.generate_text.title', 'Generate text')}
            icon={<IconSparkle />}
            onClick={handleClickGenerateText}
          />,
          <TextareaAction.Button
            id="application-description-ai-cleanup"
            label={t('l.smart_description.generate_text.clean_up.title', 'Clean up')}
            icon={<IconSparkle />}
            onClick={handleClickCleanUp}
          />,
          <TextareaAction.Button
            id="application-description-ai-tone"
            label={t('l.smart_description.change_tone.change_tone.title', 'Change tone')}
            icon={<IconSparkle />}
            onClick={handleClickChangeTone}
          />
        ]}
      />
      <InfoBox>
        <Trans i18nKey="l.listing.edit.description_adding_guide">
          Not sure what to add?{' '}
          <TextLink
            href="https://liv.rent/blog/landlords/how-to-advertise-your-rental-property/#How_to_advertise_your_property_for_rent"
            target="_blank"
          >
            <u>Read our guide on best practices</u>
          </TextLink>
        </Trans>
      </InfoBox>
    </FormSection>
  )
}

export default DescriptionField
